// © 2026 Camp Promethean, LLC. All Rights Reserved.
// Phase 7 / T-708 + T-709 — per-room AROYA configuration: map our zones to AROYA zone ids and
// edit the lights on/off photoperiod (drives the day/night rollup split). Reads AROYA zones via
// the aroya-proxy edge function.

import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, AlertCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { readPhotoperiod } from "@/lib/aroyaPhotoperiod";

export interface ConfigZone {
  id: string;
  zone_number: number;
  aroya_zone_id: string | null;
}
export interface ConfigRoom {
  id: string;
  facility_id: string;
  room_name: string;
  aroya_room_id: number | null;
  photoperiod: unknown;
  zones: ConfigZone[];
}
interface AroyaZone { id: number | string; name: string; sequence: number }

const UNMAPPED = "__none__";

export function RoomConfigDialog({
  room, open, onOpenChange, onSaved,
}: {
  room: ConfigRoom | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onSaved: () => void;
}) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [aroyaZones, setAroyaZones] = useState<AroyaZone[] | null>(null);
  const [zoneMap, setZoneMap] = useState<Record<string, string>>({});
  const [photo, setPhoto] = useState({ on: "08:00", off: "20:00" });
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !room) return;
    setPhoto(readPhotoperiod(room.photoperiod));
    setZoneMap(Object.fromEntries(
      room.zones.filter((z) => z.aroya_zone_id).map((z) => [z.id, String(z.aroya_zone_id)]),
    ));
    setAroyaZones(null);
    setLoadError(null);
    if (room.aroya_room_id == null) {
      setLoadError("Map this room to a sensor room id first, then reopen to map zones.");
      return;
    }
    (async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase.functions.invoke("aroya-proxy", {
          body: { action: "room", facilityId: room.facility_id, aroyaRoomId: room.aroya_room_id },
        });
        if (error) throw error;
        if (!data?.ok) throw new Error(data?.error || "Failed to load sensor zones");
        const zones: AroyaZone[] = data.data?.zones ?? [];
        setAroyaZones(zones);
        setZoneMap((prev) => {
          const next = { ...prev };
          for (const z of room.zones) {
            if (next[z.id]) continue;
            const match = zones.find((az) => az.sequence === z.zone_number - 1)
              ?? zones.find((az) => az.sequence + 1 === z.zone_number);
            if (match) next[z.id] = String(match.id);
          }
          return next;
        });
      } catch (e) {
        setLoadError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [open, room]);

  const save = async () => {
    if (!room) return;
    setSaving(true);
    try {
      for (const z of room.zones) {
        const val = zoneMap[z.id] && zoneMap[z.id] !== UNMAPPED ? zoneMap[z.id] : null;
        const current = z.aroya_zone_id ?? null;
        if (val === current) continue;
        const { error } = await (supabase as any)
          .from("zones").update({ aroya_zone_id: val }).eq("id", z.id);
        if (error) throw error;
      }
      const { error: pErr } = await supabase
        .from("rooms").update({ photoperiod: { on: photo.on, off: photo.off } as never })
        .eq("id", room.id);
      if (pErr) throw pErr;

      toast({ title: "Room configured", description: `${room.room_name}: zones + photoperiod saved.` });
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast({
        variant: "destructive", title: "Save failed",
        description: e instanceof Error ? e.message : "Could not save room configuration.",
      });
    } finally {
      setSaving(false);
    }
  };

  const usedAroyaIds = new Set(Object.values(zoneMap).filter((v) => v && v !== UNMAPPED));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[min(90vh,720px)] max-w-lg flex-col gap-0 overflow-hidden p-0">
        <DialogHeader className="flex-shrink-0 space-y-1.5 px-6 pt-6">
          <DialogTitle>Configure {room?.room_name}</DialogTitle>
          <DialogDescription>
            Map each zone to its sensor zone and set the lights on/off window (facility local time).
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
          {loadError && (
            <div className="mb-4 flex items-start gap-2 rounded-md bg-muted p-3 text-sm text-muted-foreground">
              <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <span>{loadError}</span>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium">Zone mapping</Label>
              {loading ? (
                <div className="flex items-center gap-2 py-4 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading sensor zones…
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Our zone</TableHead>
                      <TableHead>Sensor zone</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {room?.zones.map((z) => (
                      <TableRow key={z.id}>
                        <TableCell>Zone {z.zone_number}</TableCell>
                        <TableCell>
                          <Select
                            value={zoneMap[z.id] ?? UNMAPPED}
                            onValueChange={(v) => setZoneMap((m) => ({ ...m, [z.id]: v }))}
                            disabled={!aroyaZones}
                          >
                            <SelectTrigger className="w-full"><SelectValue placeholder="Unmapped" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value={UNMAPPED}>Unmapped</SelectItem>
                              {aroyaZones?.map((az) => {
                                const idStr = String(az.id);
                                const takenElsewhere = usedAroyaIds.has(idStr) && zoneMap[z.id] !== idStr;
                                return (
                                  <SelectItem key={idStr} value={idStr} disabled={takenElsewhere}>
                                    {az.name} (#{idStr}){takenElsewhere ? " — in use" : ""}
                                  </SelectItem>
                                );
                              })}
                            </SelectContent>
                          </Select>
                        </TableCell>
                      </TableRow>
                    ))}
                    {room && room.zones.length === 0 && (
                      <TableRow><TableCell colSpan={2} className="text-muted-foreground">
                        No zones on this room.
                      </TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
              {aroyaZones && aroyaZones.length === 0 && (
                <p className="mt-1 text-xs text-muted-foreground">
                  The sensor reports no zones for this room (environment-only). Substrate data will be empty.
                </p>
              )}
            </div>

            <div>
              <Label className="text-sm font-medium">Photoperiod (lights)</Label>
              <p className="mb-2 text-xs text-muted-foreground">
                Used for day/night averages and chart shading. Times are in your facility timezone.
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">On</span>
                  <Input type="time" value={photo.on}
                    onChange={(e) => setPhoto((p) => ({ ...p, on: e.target.value }))} className="w-32" />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Off</span>
                  <Input type="time" value={photo.off}
                    onChange={(e) => setPhoto((p) => ({ ...p, off: e.target.value }))} className="w-32" />
                </div>
                {photo.off <= photo.on && (
                  <Badge variant="secondary">overnight</Badge>
                )}
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="flex-shrink-0 border-t px-6 py-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving}>
            {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving</> : "Save configuration"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
